import React, { useState, useEffect } from 'react';
import { FaFolder } from 'react-icons/fa';
import Modal from './Modal';
import { toast } from 'react-toastify';
import { fileService } from '../services/api';

const MAX_TAGS = 1;

const CreateFolderModal = ({ isOpen, onClose, onCreateFolder }) => {
  const [folderName, setFolderName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [predefinedTagsCategory1, setPredefinedTagsCategory1] = useState([]);
  const [predefinedTagsCategory2, setPredefinedTagsCategory2] = useState([]);
  const [loadingPredefined, setLoadingPredefined] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar nombre
    if (!folderName.trim()) {
      setError('El nombre de la carpeta es requerido');
      return;
    }

    // Validar caracteres especiales
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(folderName)) {
      setError('El nombre contiene caracteres no válidos');
      return;
    }

    setCreating(true);
    setError('');
    
    console.log('🔄 Iniciando creación de carpeta:', folderName.trim());

    try {
      // Normalizar tags antes de enviarlos
      const normalizeTag = (tag) => {
        if (!tag || typeof tag !== 'string') return null;
        return tag.toLowerCase().trim().replace(/\s+/g, '-');
      };
      const normalizedTags = selectedTags.map(tag => normalizeTag(tag)).filter(tag => tag);
      
      const success = await onCreateFolder(folderName.trim(), normalizedTags.length > 0 ? normalizedTags : null);
      if (success) {
        const createdFolderName = folderName.trim();
        setFolderName('');
        setSelectedTags([]);
        setTimeout(() => {
          onClose();
          toast.success(`Carpeta "${createdFolderName}" creada exitosamente`, { autoClose: 2000 });
        }, 1000);
      } else {
        console.error('❌ Error: onCreateFolder retornó false');
        setError('Error al crear la carpeta');
      }
    } catch (err) {
      console.error('❌ Error en handleSubmit:', err);
      setError(err.message || 'Error al crear la carpeta');
    } finally {
      setCreating(false);
    }
  };

  // Cargar tags predefinidos cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadPredefinedTags();
    }
  }, [isOpen]);

  // Limpiar estado cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setFolderName('');
      setError('');
      setSelectedTags([]);
    }
  }, [isOpen]);

  const loadPredefinedTags = async () => {
    setLoadingPredefined(true);
    try {
      const response = await fileService.getPredefinedFolderTags();
      const category1Tags = response.category1 || [];
      const category2Tags = response.category2 || [];
      setPredefinedTagsCategory1(category1Tags);
      setPredefinedTagsCategory2(category2Tags);
    } catch (error) {
      console.error('Error cargando tags predefinidos de carpetas:', error);
    } finally {
      setLoadingPredefined(false);
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        // Remover tag
        return prev.filter(t => t !== tag);
      } else {
        // Agregar tag (máximo 1)
        if (prev.length >= MAX_TAGS) {
          toast.warning(`Solo se pueden seleccionar máximo ${MAX_TAGS} tags`, { autoClose: 2000 });
          return prev;
        }
        return [...prev, tag];
      }
    });
  };

  const handleClose = () => {
    setFolderName('');
    setError('');
    setSelectedTags([]);
    setCreating(false);
    onClose();
  };

  return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Crear Nueva Carpeta" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-yellow-900/20 rounded-full flex items-center justify-center">
            <FaFolder className="text-3xl text-yellow-500" />
          </div>
        </div>

        {/* Input */}
        <div>
          <label htmlFor="folderName" className="block text-sm font-medium text-gray-300 mb-2">
            Nombre de la carpeta
          </label>
          <input
            type="text"
            id="folderName"
            value={folderName}
            onChange={(e) => {
              setFolderName(e.target.value);
              setError(''); // Limpiar error al escribir
            }}
            placeholder="Mi nueva carpeta"
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
            disabled={creating}
          />
          
          {error && (
            <p className="mt-2 text-sm text-red-400">{error}</p>
          )}
        </div>

        {/* Tags Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Etiquetas (opcional, máx. {MAX_TAGS})
          </label>
          
          {loadingPredefined ? (
            <div className="text-sm text-gray-400">Cargando tags disponibles...</div>
          ) : (
            <>
              <div className="max-h-48 overflow-y-auto border border-gray-600 rounded-md p-4 bg-gray-800 space-y-4">
                {/* Categoría 1 - Amarillo */}
                {predefinedTagsCategory1.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Tag carpeta</p>
                    <div className="flex flex-wrap gap-2">
                      {predefinedTagsCategory1.map((tag) => {
                        const isSelected = selectedTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => handleTagToggle(tag)}
                            disabled={creating}
                            className={`
                              inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border
                              transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500/40
                              ${isSelected
                                ? 'bg-yellow-600 text-white border-yellow-500 shadow-lg shadow-yellow-500/25'
                                : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white hover:border-gray-500'
                              }
                              ${creating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                          >
                            {tag}
                            {isSelected && (
                              <span className="ml-2 text-xs">✓</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Categoría 2 - Verde */}
                {predefinedTagsCategory2.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Tag Tecweek</p>
                    <div className="flex flex-wrap gap-2">
                      {predefinedTagsCategory2.map((tag) => {
                        const isSelected = selectedTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => handleTagToggle(tag)}
                            disabled={creating}
                            className={`
                              inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border
                              transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500/40
                              ${isSelected
                                ? 'bg-green-600 text-white border-green-500 shadow-lg shadow-green-500/25'
                                : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white hover:border-gray-500'
                              }
                              ${creating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                          >
                            {tag}
                            {isSelected && (
                              <span className="ml-2 text-xs">✓</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {selectedTags.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-400 mb-2">
                    Tags seleccionados ({selectedTags.length}/{MAX_TAGS}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTags.map((tag) => {
                      const isCategory2 = predefinedTagsCategory2.includes(tag);
                      return (
                        <span
                          key={tag}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                            isCategory2
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                          }`}
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleTagToggle(tag)}
                            className={`ml-2 ${isCategory2 ? 'text-green-600 hover:text-green-800' : 'text-yellow-600 hover:text-yellow-800'}`}
                            disabled={creating}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Help text */}
        <div className="text-xs text-gray-500">
          <p>• No uses caracteres especiales: &lt; &gt; : " / \ | ? *</p>
          <p>• El nombre debe ser único en la ubicación actual</p>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={creating}
            className="px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!folderName.trim() || creating}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {creating ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                <span>Creando...</span>
              </>
            ) : (
              <>
                <FaFolder />
                <span>Crear Carpeta</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateFolderModal; 